import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { resolveTenantSlug } from "../lib/api";

/**
 * TenantContext — single source of truth for the active tenant slug across
 * the React tree. Mirrors what `api.js` injects into the X-Tenant-Slug
 * header. Updating `setTenantSlug()` persists the choice in localStorage
 * and triggers a re-fetch of branding/catalog by remounting consumers.
 */
const TenantContext = createContext({
  tenantSlug: "default",
  setTenantSlug: () => {},
  isOverridden: false,
});

export function TenantProvider({ children }) {
  const [tenantSlug, setSlug] = useState(() => resolveTenantSlug());
  const [isOverridden, setOverridden] = useState(() => {
    try { return !!localStorage.getItem("kb_tenant"); } catch { return false; }
  });

  const setTenantSlug = useCallback((next) => {
    try {
      if (!next || next === "default") localStorage.removeItem("kb_tenant");
      else localStorage.setItem("kb_tenant", next);
    } catch { /* private mode */ }
    setSlug(next || "default");
    setOverridden(!!next && next !== "default");
    // Force a full page reload so every cached fetch + service worker scope
    // re-runs against the new tenant. This is intentional — multi-tenant
    // boundaries are a security/data line, not a soft UI toggle.
    setTimeout(() => window.location.reload(), 60);
  }, []);

  // Keep the resolver in sync if URL or storage changes externally
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "kb_tenant") setSlug(resolveTenantSlug());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <TenantContext.Provider value={{ tenantSlug, setTenantSlug, isOverridden }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
